import React, { useState } from "react";
import {
  Box,
  Button,
  Typography,
  Collapse,
  Paper,
  Fade,
  Divider,
  CircularProgress,
  Grid,
  Stack,
} from "@mui/material";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { useQuery } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import { QK } from "../../../../../base/qk";
import { getActiveCarePlans } from "../../../../../api/services/carePathWay";
import useClientDetails from "../../../../../hooks/useClientDetails";

const ActiveCareTabs = () => {
  const [showData, setShowData] = useState(false);
  const navigate = useNavigate();
  const { id: clientID } = useParams();
  const { fullName } = useClientDetails(clientID);

  const { data: activeCarePlans = [], isLoading } = useQuery(
    [QK.activeCarePlans, { client: clientID }],
    () => getActiveCarePlans({ client: clientID })
  );

  const handleShowClick = () => setShowData(!showData);

  const handlePlanClick = (planId, assignedCarePlanID) => {
    navigate(`/${clientID}/careplan/${planId}/${assignedCarePlanID}`);
  };

  return (
    <Box sx={{ width: "100%", mb: 3 }}>
      {/* Header Button - Modern Gradient */}
      <Button
        fullWidth
        onClick={handleShowClick}
        variant="contained"
        sx={{
          justifyContent: "space-between",
          py: 2,
          px: 3,
          borderRadius: "12px",
          fontWeight: 700,
          textTransform: "none",
          fontSize: "1rem",
          background: showData
            ? "linear-gradient(135deg, #065f46, #047857)" // Deep Emerald
            : "linear-gradient(135deg, #10b981, #059669)", // Bright Emerald
          color: "white",
          boxShadow: "0 4px 12px rgba(16, 185, 129, 0.2)",
          "&:hover": {
            background: "linear-gradient(135deg, #059669, #047857)",
            boxShadow: "0 6px 15px rgba(16, 185, 129, 0.3)",
          },
        }}
      >
        <span>Active Care Plans for {fullName}</span>
        <Typography variant="caption" sx={{ bgcolor: 'rgba(255,255,255,0.2)', px: 1.5, py: 0.5, borderRadius: '20px' }}>
          {activeCarePlans.length} Active
        </Typography>
      </Button>

      {/* Collapsible Content */}
      <Collapse in={showData}>
        <Fade in={showData} timeout={400}>
          <Box
            sx={{
              mt: 2,
              borderRadius: "16px",
              p: 3,
              bgcolor: "#f0fdf4", // Light emerald bg
              border: "1px solid #dcfce7",
            }}
          >
            {isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress size={30} color="success" />
              </Box>
            ) : activeCarePlans.length === 0 ? (
              <Typography align="center" sx={{ py: 4, color: "text.secondary", fontStyle: 'italic' }}>
                No active care plans found.
              </Typography>
            ) : (
              <Grid container spacing={2}>
                {activeCarePlans.map((plan) => (
                  <Grid item xs={12} sm={6} md={4} key={plan.id}>
                    <Paper
                      onClick={() => handlePlanClick(plan.form, plan.id)}
                      elevation={0}
                      sx={{
                        p: 2.5,
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        border: "1px solid #bbf7d0",
                        borderRadius: "14px",
                        bgcolor: "white",
                        cursor: "pointer",
                        transition: "all 0.2s ease-in-out",
                        position: 'relative',
                        overflow: 'hidden',
                        "&:hover": {
                          borderColor: "#10b981",
                          transform: "translateY(-4px)",
                          boxShadow: "0 12px 20px rgba(16, 185, 129, 0.08)",
                          "& .arrow-icon": { transform: "translateX(4px)" }
                        },
                      }}
                    >
                      {/* Top Section: Status & Title */}
                      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 1.5 }}>
                        <FiberManualRecordIcon 
                          sx={{ 
                            fontSize: 14, 
                            color: "#10b981", 
                            mt: 0.5,
                            filter: "drop-shadow(0 0 4px #10b981)" 
                          }} 
                        />
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#064e3b", lineHeight: 1.2 }}>
                            {plan.form_title}
                          </Typography>
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              color: "#059669", 
                              fontWeight: 600, 
                              fontSize: '0.65rem',
                              textTransform: 'uppercase',
                              letterSpacing: 0.5
                            }}
                          >
                            Live Pathway
                          </Typography>
                        </Box>
                      </Box>

                      <Divider sx={{ my: 1.5, borderStyle: 'dashed', borderColor: '#dcfce7' }} />

                      {/* Description Area */}
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: "#374151", 
                          fontSize: '0.85rem',
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          mb: 2
                        }}
                      >
                        {plan.description || "Comprehensive care guidelines and pathway details."}
                      </Typography>

                      {/* Footer: Action Hint */}
                      <Stack 
                        direction="row" 
                        spacing={1} 
                        alignItems="center" 
                        sx={{ mt: 'auto', color: '#10b981' }}
                      >
                        <Typography variant="button" sx={{ fontSize: '0.7rem', fontWeight: 700 }}>
                          View Details
                        </Typography>
                        <ArrowForwardIosIcon className="arrow-icon" sx={{ fontSize: 10, transition: '0.2s' }} />
                      </Stack>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        </Fade>
      </Collapse>
    </Box>
  );
};

export default ActiveCareTabs;